import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Wand2, Cpu, Zap, ShieldCheck, ArrowRight, Sparkles, Check, Mic, Video, Share2, Scissors } from "lucide-react";
import { LangToggle, useT } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { ORG_LD, Seo } from "@/components/Seo";

const heroImg = "https://images.unsplash.com/photo-1689443111130-6e9c7dfd8f9e?crop=entropy&cs=srgb&fm=jpg&q=85";

const STEPS = [
  { n: "01", title: "Describe your idea", desc: "Type one prompt — a video ad, an image, an app blueprint, or a campaign — or upload your own footage." },
  { n: "02", title: "Watch the pipeline", desc: "The Concierge plans the steps and routes each one to a real engine: AI script writer, cinematic video engine, HD voice, music generator." },
  { n: "03", title: "Edit and ship", desc: "Trim, reframe to 9:16 / 1:1 / 4:5, duck music under the voice, add auto-captions, download the MP4 or publish once your channel is connected." },
];

const FEATURES = [
  { icon: Video, label: "Real Video Synthesis", desc: "AI video clips + full-length Cinematic Assembly (15s–10 min)" },
  { icon: Mic, label: "Voice & Dubbing", desc: "HD AI voice-over · 22+ languages" },
  { icon: Scissors, label: "Edit & Caption", desc: "Trim · merge · reframe · music ducking · auto-captions" },
  { icon: Share2, label: "Distribution Hub", desc: "YouTube / Meta via your own OAuth, or download & post manually" },
];


export default function Login() {
  const { t, lang } = useT();
  const hi = lang === "hi";
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [health, setHealth] = useState(null);

  useEffect(() => { if (!loading && user) navigate("/studio", { replace: true }); }, [user, loading, navigate]);
  useEffect(() => {
    api.get("/billing/packs").then((r) => setPacks(r.data.packs || [])).catch(() => {});
    api.get("/health").then((r) => setHealth(r.data)).catch(() => {});
  }, []);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/studio";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#07080E] text-slate-100 relative overflow-hidden grain">
      <Seo path="/" title={hi ? "Vectra — AI वीडियो, वॉइस-ओवर और रील्स स्टूडियो (भारत)" : "Vectra — AI video, voice-over & reels studio (India)"} description={hi ? "एक प्रॉम्प्ट से पूरा प्रोडक्शन: स्क्रिप्ट, AI विज़ुअल, सिनेमैटिक वीडियो, HD वॉइस-ओवर, म्यूज़िक, कैप्शन और वर्टिकल रील्स — सब डाउनलोड करने योग्य फ़ाइलें। प्रीपेड पैक, UPI से भुगतान।" : "Turn one prompt into a real production: scripts, AI visuals, cinematic video, HD voice-over, music, captions and vertical reels — all downloadable files. Prepaid packs, pay by UPI."} jsonLd={[ORG_LD]} />
      <div className="absolute inset-0 grid-bg opacity-30" />
      <nav className="relative z-20 h-16 px-6 sm:px-10 flex items-center justify-between border-b border-white/10">
        <Logo size={34} />
        <div className="flex items-center gap-2">
          <LangToggle />
          <button data-testid="nav-login-button" onClick={() => navigate("/auth")} className="text-sm font-semibold px-4 py-2 rounded-full border border-white/15 hover:border-purple-500/40 hover:bg-white/5 transition-colors">{t("Sign in")}</button>
          <button data-testid="nav-signup-button" onClick={() => navigate("/auth?mode=signup")} className="text-sm font-semibold px-4 py-2 rounded-full bg-gradient-to-r from-purple-600 to-cyan-600 text-white hover:scale-[1.03] transition-transform">{t("Create account")}</button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 px-6 sm:px-10 lg:px-20 pt-16 pb-24">
        <div aria-hidden="true" role="presentation" className="absolute top-0 right-0 w-[45%] h-full opacity-20 bg-cover bg-center hidden lg:block" style={{ backgroundImage: `url(${heroImg})`, maskImage: "linear-gradient(to left, black, transparent)" }} />
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-8 text-xs font-mono uppercase tracking-widest text-purple-300">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> AI audio · video · music production studio
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            {hi ? <>AI वीडियो, वॉइस-ओवर और रील्स स्टूडियो<br /><span className="text-gradient">— एक प्रॉम्प्ट से</span></> : <>AI video, voice-over &amp; reels studio<br /><span className="text-gradient">— from one prompt</span></>}
          </h1>
          <p data-testid="home-brand-subtitle" className="mt-3 font-mono text-xs sm:text-sm uppercase tracking-widest text-cyan-400/80">Vectra Cinematic Magic Studio Pro · Swastik AI Labs</p>
          <p className="mt-6 text-slate-400 text-base sm:text-lg max-w-xl leading-relaxed">
            {hi ? "एक प्रॉम्प्ट असली प्रोडक्शन पाइपलाइन बनाता है — AI स्क्रिप्ट राइटर लिखता है, वीडियो इंजन रेंडर करता है, HD वॉइस आवाज़ देती है, म्यूज़िक जनरेटर स्कोर बनाता है और स्टूडियो एडिटर फ़ाइनल फ़ाइल को कट, रीफ़्रेम और कैप्शन करता है। हर आउटपुट एक डाउनलोड करने योग्य एसेट है, कभी मॉक नहीं।" : "One prompt plans a real production pipeline — the AI script writer writes, the video engine renders, HD voice narrates, the music generator scores it and the studio editor cuts, reframes and captions the final file. Every output is a downloadable asset, never a mock."}
          </p>
          {health && (
            <div data-testid="live-health-strip" className="mt-5 flex flex-wrap gap-2 text-[11px] font-mono">
              <span className="px-2.5 py-1 rounded-full border border-emerald-500/30 text-emerald-300 bg-emerald-500/10">● {health.db ? "platform online" : "degraded"}</span>
              <span className="px-2.5 py-1 rounded-full border border-white/10 text-slate-400">{health.providers?.text?.length || 0} text models · {health.providers?.video?.length || 0} video engines</span>
              <span className="px-2.5 py-1 rounded-full border border-white/10 text-slate-400">{health.media_total} real assets rendered</span>
              <span className="px-2.5 py-1 rounded-full border border-white/10 text-slate-400">{health.renders_24h?.completed ?? 0} renders completed · 24h</span>
            </div>
          )}
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button data-testid="google-login-button" onClick={handleLogin}
              className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-gradient-to-r from-purple-600 via-indigo-600 to-cyan-600 text-white font-semibold pulse-glow hover:scale-[1.02] transition-transform">
              <Sparkles size={18} /> Enter the Studio with Google
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button data-testid="hero-signup-button" onClick={() => navigate("/auth?mode=signup")} className="text-sm font-semibold px-6 py-4 rounded-full border border-white/15 hover:bg-white/5 transition-colors">Create account with email</button>
            <span className="text-xs font-mono text-slate-500">Google · Email · WhatsApp-verified</span>
          </div>

          <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {FEATURES.map((f, i) => (
              <motion.div key={f.label} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 + i * 0.08 }}
                className="glass rounded-2xl p-5 hover:border-purple-500/40 transition-colors">
                <f.icon size={22} className="text-cyan-400 mb-3" />
                <div className="font-display font-semibold text-sm">{f.label}</div>
                <div className="text-xs text-slate-500 mt-1">{f.desc}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How it works */}
      <section data-testid="how-it-works" className="relative z-10 px-6 sm:px-10 lg:px-20 py-16 border-t border-white/10">
        <div className="text-xs font-mono uppercase tracking-widest text-purple-400 mb-2">How it works</div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-10">Idea to finished asset in three steps</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <motion.div key={s.n} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="glass rounded-2xl p-6">
              <div className="font-mono text-3xl font-extrabold text-gradient mb-3">{s.n}</div>
              <div className="font-display font-semibold">{s.title}</div>
              <div className="text-sm text-slate-500 mt-2">{s.desc}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section data-testid="pricing" className="relative z-10 px-6 sm:px-10 lg:px-20 py-16 border-t border-white/10">
        <div className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2">Pricing</div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-2">Prepaid packs · 30 days · pay by UPI</h2>
        <p className="text-sm text-slate-500 mb-10">Quotas below are the live limits enforced by the platform. Standard license carries a small Vectra watermark; the Copyright-Free license removes it.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl">
          {packs.filter((t) => t.id !== "free").map((t, i) => (
            <motion.div key={t.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              data-testid={`pricing-${t.id}`}
              className={`rounded-2xl p-6 relative ${t.highlight ? "border-2 border-purple-500/50 bg-gradient-to-b from-purple-600/10 to-transparent" : "glass"}`}>
              {t.highlight && <div className="absolute -top-3 left-6 text-[10px] font-mono uppercase px-2 py-1 rounded-full bg-gradient-to-r from-purple-600 to-cyan-600 text-white">Most popular</div>}
              <div className="font-display font-bold text-lg">{t.name}</div>
              <div className="mt-2 flex items-baseline gap-1"><span className="text-3xl font-extrabold font-display">₹{t.price.toLocaleString("en-IN")}</span><span className="text-slate-500 text-sm">/ {t.validity_days} days</span></div>
              <div className="text-[10px] font-mono text-amber-300 mt-1">+₹{(t.cf_price - t.price).toLocaleString("en-IN")} for a copyright-free license · GST incl.</div>
              <div className="mt-5 space-y-2.5">
                {(t.features || []).map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm text-slate-300"><Check size={15} className="text-emerald-400 shrink-0" /> {f}</div>
                ))}
              </div>
              <button onClick={() => navigate(`/billing?pack=${t.id}`)} className={`mt-6 w-full py-2.5 rounded-full text-sm font-semibold transition-transform hover:scale-[1.02] ${t.highlight ? "bg-gradient-to-r from-purple-600 to-cyan-600 text-white" : "border border-white/15 hover:bg-white/5"}`}>
                Get started
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      <div className="relative z-10"><Footer /></div>
    </div>
  );
}
